/**
 * RoleTemplateRepository.ts - RBAC Role Template Repository
 *
 * FERP Repository Pattern - Frontend data access layer for role template operations
 *
 * GitHub Issue: #337
 * Week: 1 (Backend + Frontend Implementation)
 *
 * FERP Principles:
 * - Repository Pattern: All template data access through this layer
 * - Data-Driven: All values from GraphQL backend
 * - Immutable Audit: All operations tracked
 * - Multi-Tenant: Complete tenant isolation
 * - Type-Safe: Full TypeScript type definitions
 * - Backend ID Generation: Uses backend Snowflake IDs
 */

import { ApolloClient, gql } from '@apollo/client';
import { BaseRepository } from './BaseRepository';

// ============================================================================
// Type Definitions (matching backend GraphQL schema)
// ============================================================================

/**
 * Template Role Definition
 *
 * Role configuration within a template (before instantiation)
 */
export interface TemplateRoleDefinition {
  roleName: string;
  displayName: string;
  description?: string;
  scopes: string[];
  capabilities: string[];
  inheritsFrom: string[];
}

/**
 * Role Template
 *
 * Reusable role configuration with metadata
 */
export interface RoleTemplate {
  templateId: string;
  tenantId: string;
  templateName: string;
  displayName: string;
  description: string;
  category: string;
  complianceFrameworks: string[];
  roleDefinition: TemplateRoleDefinition;
  version: number;
  status: 'active' | 'draft' | 'archived';
  usageCount: number;
  createdAt: string;
  createdBy: string;
}

/**
 * Role Definition (instantiated from template)
 *
 * Actual role instance created from template
 */
export interface RoleDefinition {
  roleId: string;
  tenantId: string;
  roleName: string;
  displayName: string;
  description?: string;
  scopes: string[];
  capabilities: string[];
  inheritsFrom: string[];
  enabled: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

// ============================================================================
// Input Types (for mutations)
// ============================================================================

export interface CreateRoleTemplateInput {
  tenantId: string;
  templateName: string;
  displayName: string;
  description: string;
  category: string;
  complianceFrameworks: string[];
  roleDefinition: TemplateRoleDefinitionInput;
}

export interface TemplateRoleDefinitionInput {
  roleName: string;
  displayName: string;
  description?: string;
  scopes: string[];
  capabilities: string[];
  inheritsFrom: string[];
}

export interface InstantiateTemplateInput {
  templateId: string;
  tenantId: string;
  roleNameOverride?: string;
}

// ============================================================================
// GraphQL Fragments
// ============================================================================

const TEMPLATE_ROLE_DEFINITION_FRAGMENT = gql`
  fragment TemplateRoleDefinitionFields on TemplateRoleDefinition {
    roleName
    displayName
    description
    scopes
    capabilities
    inheritsFrom
  }
`;

const ROLE_TEMPLATE_FRAGMENT = gql`
  fragment RoleTemplateFields on RoleTemplate {
    templateId
    tenantId
    templateName
    displayName
    description
    category
    complianceFrameworks
    roleDefinition {
      ...TemplateRoleDefinitionFields
    }
    version
    status
    usageCount
    createdAt
    createdBy
  }
  ${TEMPLATE_ROLE_DEFINITION_FRAGMENT}
`;

const ROLE_DEFINITION_FRAGMENT = gql`
  fragment RoleDefinitionFields on RoleDefinition {
    roleId
    tenantId
    roleName
    displayName
    description
    scopes
    capabilities
    inheritsFrom
    enabled
    createdAt
    createdBy
    updatedAt
    updatedBy
  }
`;

// ============================================================================
// GraphQL Queries
// ============================================================================

const LIST_ROLE_TEMPLATES = gql`
  query ListRoleTemplates($tenantId: String!) {
    listRoleTemplates(tenantId: $tenantId) {
      ...RoleTemplateFields
    }
  }
  ${ROLE_TEMPLATE_FRAGMENT}
`;

const GET_ROLE_TEMPLATE = gql`
  query GetRoleTemplate($templateId: String!, $tenantId: String!) {
    getRoleTemplate(templateId: $templateId, tenantId: $tenantId) {
      ...RoleTemplateFields
    }
  }
  ${ROLE_TEMPLATE_FRAGMENT}
`;

const LIST_TEMPLATES_BY_CATEGORY = gql`
  query ListTemplatesByCategory($tenantId: String!, $category: String!) {
    listTemplatesByCategory(tenantId: $tenantId, category: $category) {
      ...RoleTemplateFields
    }
  }
  ${ROLE_TEMPLATE_FRAGMENT}
`;

// ============================================================================
// GraphQL Mutations
// ============================================================================

const CREATE_ROLE_TEMPLATE = gql`
  mutation CreateRoleTemplate($input: CreateRoleTemplateInput!) {
    createRoleTemplate(input: $input) {
      ...RoleTemplateFields
    }
  }
  ${ROLE_TEMPLATE_FRAGMENT}
`;

const UPDATE_ROLE_TEMPLATE = gql`
  mutation UpdateRoleTemplate(
    $templateId: String!
    $tenantId: String!
    $displayName: String
    $description: String
    $category: String
  ) {
    updateRoleTemplate(
      templateId: $templateId
      tenantId: $tenantId
      displayName: $displayName
      description: $description
      category: $category
    ) {
      ...RoleTemplateFields
    }
  }
  ${ROLE_TEMPLATE_FRAGMENT}
`;

const DELETE_ROLE_TEMPLATE = gql`
  mutation DeleteRoleTemplate($templateId: String!, $tenantId: String!) {
    deleteRoleTemplate(templateId: $templateId, tenantId: $tenantId)
  }
`;

const INSTANTIATE_TEMPLATE = gql`
  mutation InstantiateTemplate($input: InstantiateTemplateInput!) {
    instantiateTemplate(input: $input) {
      ...RoleDefinitionFields
    }
  }
  ${ROLE_DEFINITION_FRAGMENT}
`;

// ============================================================================
// Repository Implementation
// ============================================================================

/**
 * Role Template Repository
 *
 * FERP Repository Pattern:
 * - Single source of truth for role template data
 * - GraphQL backend integration
 * - Complete type safety
 * - Audit trail integration
 * - Multi-tenant isolation
 *
 * GitHub Issue: #337
 */
export class RoleTemplateRepository extends BaseRepository<RoleTemplate> {
  private apolloClient: ApolloClient;

  constructor(client: ApolloClient) {
    super();
    this.apolloClient = client;
  }

  // ==========================================================================
  // Query Methods
  // ==========================================================================

  /**
   * List all templates for a tenant
   *
   * @param tenantId - Tenant identifier
   * @returns Array of role templates
   */
  async listTemplates(tenantId: string): Promise<RoleTemplate[]> {
    const result = await this.apolloClient.query<Record<string, any>>({
      query: LIST_ROLE_TEMPLATES,
      variables: { tenantId },
      fetchPolicy: 'network-only', // Always fetch latest templates
    });

    return result.data?.listRoleTemplates ?? null;
  }

  /**
   * Get single template by ID
   *
   * @param templateId - Template identifier
   * @param tenantId - Tenant identifier
   * @returns Role template or null
   */
  async getTemplate(
    templateId: string,
    tenantId: string
  ): Promise<RoleTemplate | null> {
    const result = await this.apolloClient.query<Record<string, any>>({
      query: GET_ROLE_TEMPLATE,
      variables: { templateId, tenantId },
      fetchPolicy: 'network-only',
    });

    return result.data?.getRoleTemplate ?? null;
  }

  /**
   * List templates by category
   *
   * @param tenantId - Tenant identifier
   * @param category - Template category (healthcare, financial, operational, compliance, custom)
   * @returns Array of role templates
   */
  async listTemplatesByCategory(
    tenantId: string,
    category: string
  ): Promise<RoleTemplate[]> {
    const result = await this.apolloClient.query<Record<string, any>>({
      query: LIST_TEMPLATES_BY_CATEGORY,
      variables: { tenantId, category },
      fetchPolicy: 'network-only',
    });

    return result.data?.listTemplatesByCategory ?? null;
  }

  // ==========================================================================
  // Mutation Methods
  // ==========================================================================

  /**
   * Create new role template
   *
   * @param input - Template creation input
   * @returns Created template
   */
  async createTemplate(input: CreateRoleTemplateInput): Promise<RoleTemplate> {
    const result = await this.apolloClient.mutate<Record<string, any>>({
      mutation: CREATE_ROLE_TEMPLATE,
      variables: { input },
      refetchQueries: ['ListRoleTemplates'],
    });

    if (!result.data?.createRoleTemplate) {
      throw new Error('Failed to create role template');
    }

    return result.data?.createRoleTemplate ?? null;
  }

  /**
   * Update existing template
   *
   * @param templateId - Template identifier
   * @param tenantId - Tenant identifier
   * @param updates - Fields to update
   * @returns Updated template
   */
  async updateTemplate(
    templateId: string,
    tenantId: string,
    updates: {
      displayName?: string;
      description?: string;
      category?: string;
    }
  ): Promise<RoleTemplate> {
    const result = await this.apolloClient.mutate<Record<string, any>>({
      mutation: UPDATE_ROLE_TEMPLATE,
      variables: {
        templateId,
        tenantId,
        ...updates,
      },
      refetchQueries: ['ListRoleTemplates', 'GetRoleTemplate'],
    });

    if (!result.data?.updateRoleTemplate) {
      throw new Error('Failed to update role template');
    }

    return result.data?.updateRoleTemplate ?? null;
  }

  /**
   * Delete template
   *
   * @param templateId - Template identifier
   * @param tenantId - Tenant identifier
   * @returns Success boolean
   */
  async deleteTemplate(templateId: string, tenantId: string): Promise<boolean> {
    const result = await this.apolloClient.mutate<Record<string, any>>({
      mutation: DELETE_ROLE_TEMPLATE,
      variables: { templateId, tenantId },
      refetchQueries: ['ListRoleTemplates'],
    });

    return result.data?.deleteRoleTemplate ?? false;
  }

  /**
   * Instantiate template as actual role
   *
   * Creates a new RoleDefinition from the template
   *
   * @param input - Instantiation input
   * @returns Created role definition
   */
  async instantiateTemplate(
    input: InstantiateTemplateInput
  ): Promise<RoleDefinition> {
    const result = await this.apolloClient.mutate<Record<string, any>>({
      mutation: INSTANTIATE_TEMPLATE,
      variables: { input },
      refetchQueries: ['ListRoles', 'GetRoleTemplate'],
    });

    if (!result.data?.instantiateTemplate) {
      throw new Error('Failed to instantiate template');
    }

    return result.data?.instantiateTemplate ?? null;
  }

  // ==========================================================================
  // Helper Methods
  // ==========================================================================

  /**
   * Get available template categories
   *
   * @returns Array of category names
   */
  getCategories(): string[] {
    return ['healthcare', 'financial', 'operational', 'compliance', 'custom'];
  }

  /**
   * Get compliance frameworks
   *
   * @returns Array of framework names
   */
  getComplianceFrameworks(): string[] {
    return ['HIPAA', 'SOX', 'GDPR', 'PCI-DSS', 'ISO-27001', 'FDA-21-CFR-11'];
  }
}

/**
 * Create Role Template Repository instance
 *
 * Factory function following FERP repository pattern
 *
 * @param client - Apollo Client instance
 * @returns Repository instance
 */
export function createRoleTemplateRepository(
  client: ApolloClient
): RoleTemplateRepository {
  return new RoleTemplateRepository(client);
}
